import React, { useEffect, useState } from 'react';
import { Input, Text } from 'src/@vymo/ui/atoms';
import Card from 'src/@vymo/ui/blocks/card';
import Modal, { Body, Header } from 'src/@vymo/ui/blocks/modal';
import { Map, Marker } from '@vis.gl/react-google-maps';
import { ReactComponent as LocationIcon } from 'src/assets/icons/location.svg';
import { locale } from 'src/i18n';
import keys from 'src/i18n/keys';
import AdditionalInputs from './additionalInputs';
import { PlaceAutocomplete } from './autocomplete';
import useGeocoding from './useGeocoding';
import useLocationValue from './useLocationValue';
import styles from './index.module.scss';

export type Props = {
  value: any;
  onChange: (selectedLocation: any, event?: any, additionalData?: any) => void;
  type?: string;
  subType?: string;
  'data-test-id'?: string;
  disabled?: boolean;
  showDisabledIcon?: boolean;
  viewMode?: boolean;
};

export default function LocationInput({
  onChange,
  value,
  type,
  subType,
  'data-test-id': dataTestId,
  disabled = false,
  ...props
}: Props) {
  const [showMapModal, setShowMapModal] = useState(false);
  const locationType = subType || type;

  const locationValue = useLocationValue({
    value,
    type: locationType,
  });
  const { geocodedValue, setDraggedPosition } = useGeocoding(locationValue);

  useEffect(() => {
    if (locationType === 'check_in') {
      onChange(geocodedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    geocodedValue?.geometry?.location?.lat?.(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    geocodedValue?.geometry?.location?.lng?.(),
  ]);

  return (
    <>
      <Input
        iconRight={<LocationIcon />}
        value={geocodedValue?.formatted_address || ''}
        placeholder="Enter location"
        onClick={!disabled ? () => setShowMapModal(!showMapModal) : undefined}
        disabled={disabled}
        data-test-id={dataTestId}
        {...props}
      />
      {showMapModal && (
        <Modal
          open={showMapModal}
          classNames={styles.location_modal}
          onBackdropClick={() => setShowMapModal(false)}
          data-test-id={`${dataTestId}_modal`}
          showCloseButton
          onClose={() => setShowMapModal(false)}
        >
          <Header className={styles.location_modal__header}>
            <Text type="h5">{locale(keys.ADDRESS_FIELD_HEADER)}</Text>
          </Header>
          <Body>
            <div className={styles.location_modal__wrapper}>
              <div className={styles.location_modal__wrapper__map}>
                <Map
                  defaultZoom={3}
                  defaultCenter={{ lat: 0, lng: 0 }}
                  gestureHandling="greedy"
                  disableDefaultUI
                >
                  {geocodedValue?.geometry?.location && (
                    <Marker
                      position={{
                        lat: geocodedValue.geometry?.location?.lat() || 0,
                        lng: geocodedValue.geometry?.location?.lng() || 0,
                      }}
                      title={geocodedValue?.formatted_address}
                      draggable
                      onDragEnd={(e) => setDraggedPosition(e.latLng)}
                    />
                  )}
                </Map>
                <div
                  id="customInput"
                  className={styles.location_modal__customInput}
                >
                  <PlaceAutocomplete
                    onPlaceSelect={(place) => {
                      onChange(place);
                    }}
                    selectedPlace={geocodedValue}
                  />
                </div>
              </div>
              <Card classNames={styles.location_modal__wrapper__inputsCard}>
                <AdditionalInputs
                  value={geocodedValue}
                  onChange={onChange}
                  onClose={() => setShowMapModal(false)}
                />
              </Card>
            </div>
          </Body>
        </Modal>
      )}
    </>
  );
}
